import { groupBy, sortBy } from "es-toolkit";
import { runInAction } from "mobx";

import { editNode } from "@/input/utils/editOperators";
import { ConstantNodeModel } from "@/models/nodes/ConstantNodeModel";
import { FunctionNodeModel } from "@/models/nodes/FunctionNodeModel";
import { uniqNodeNameKey } from "@/models/nodes/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";

import type { ViewerNode } from "@/components/doc/right-sidebar/node-viewer/types";
import type { DocumentModel } from "@/models/DocumentModel";
import type { GroupKeyResult } from "@/models/NodeLookupModel";
import type { NodeModel } from "@/models/nodes/types";
import type { TailwindColor } from "@/utils/tailwindColors";

function isMatchForNodeViewer(node: NodeModel): node is Maybe<ViewerNode> {
  if (node.is(FunctionNodeModel)) {
    return !node.isBuiltIn;
  }

  return node.is(VariableNodeModel) || node.is(ConstantNodeModel);
}

const TYPE_SORT_ORDER = ["variable", "constant", "function"] as const;

function groupNodesByType<T extends NodeModel>(nodes: T[]): GroupKeyResult[] {
  const viewerNodes = nodes.filter(isMatchForNodeViewer) as ViewerNode[];
  const sortedByType = sortBy(viewerNodes, [
    (n) => TYPE_SORT_ORDER.indexOf(n.type),
    (n) => n.value,
  ]);

  return Object.values(groupBy(sortedByType, uniqNodeNameKey)).map((group) => {
    return {
      base: group[0].value ?? "",
      type: group[0].type,
      ids: new Set(group.map((n) => n.id)),
    };
  });
}

/**
 * Retrieves and groups nodes by value from a document.
 * Note: Implemented for `VariableNodeModel`, `ConstantNodeModel` and `FunctionNodeModel`
 *
 * @param doc
 * @returns A two dimensional array, where each inner array contains nodes that share the same value.
 * The array is derived either from the selected nodes in the document or from all VariableNodeModel nodes if no nodes are selected.
 */
export function getGroupObjects(doc: DocumentModel): GroupKeyResult[] {
  const { allSelectedNodes } = doc.selection;

  if (allSelectedNodes.length) {
    return groupNodesByType(allSelectedNodes);
  }
  const groupedNodes = [
    ...(doc.nodeLookup
      ?.groupByKey({
        uniqBy: "labels",
        filter: (_baseKey, type, ids) => {
          if (type === "constant" || type === "variable") {
            return true;
          }

          if (type === "function") {
            const node = doc.nodes.get(ids.values().next().value!);
            return node?.is(FunctionNodeModel) ? !node.isBuiltIn : false;
          }

          return false;
        },
      })
      .values() ?? []),
  ];

  const sortedByType = sortBy(groupedNodes, [
    (n) => TYPE_SORT_ORDER.indexOf(n.type),
    (n) => n.base,
  ]);

  return sortedByType;
}

export function getNodesByValue(node: ViewerNode) {
  const key = uniqNodeNameKey(node);
  const nodes = node.doc.getNodesByType(node.type);
  return nodes.filter((n) => uniqNodeNameKey(n) === key);
}

/**
 * Sets the value of one or more nodes and updates their color and name if they exist for the new value.
 * Note: Implemented for `VariableNodeModel`, `ConstantNodeModel` and `FunctionNodeModel`
 * @param valueNode - node or array of nodes to be edited.
 * @param value - The new value to be assigned to the variable nodes.
 */
export function setNodeValue(valueNode: ViewerNode | ViewerNode[], value: string) {
  const nodes = Array.isArray(valueNode) ? valueNode : [valueNode];
  const [first] = nodes;
  const { doc } = first;

  doc.transact(
    () => {
      nodes.forEach((node) => {
        node.value = value;
      });
    },
    "setNodeValue",
    { runInAction: true },
  );

  copyFieldsFromNode(first, nodes);
}

export function setNodeName(node: ViewerNode, value: string) {
  const nodes = getNodesByValue(node);
  node.doc.transact(() => {
    runInAction(() => {
      nodes.forEach((n) => {
        n.name = value;
      });
    });
  }, "setNodeName");
}

export function setNodeColor(node: ViewerNode, value: TailwindColor) {
  const nodes = getNodesByValue(node);

  node.doc.transact(() => {
    runInAction(() => {
      nodes.forEach((n) => {
        n.color = value;
      });
    });
  }, "setNodeColor");
}

export function setNodeType(node: ViewerNode | ViewerNode[], type: ViewerNode["type"]) {
  const nodes = Array.isArray(node) ? node : [node];
  const [main] = nodes;
  const { color, name, doc } = main;
  const props = { color, name };

  const updatedNodes: ViewerNode[] = [];
  let newNode: NodeModel | null = null;

  doc.transact(
    () => {
      nodes.forEach((n) => {
        if (!n.value) {
          return;
        }

        switch (type) {
          case "variable": {
            newNode = doc.create(VariableNodeModel, n.value, props);
            break;
          }

          case "constant": {
            newNode = doc.create(ConstantNodeModel, n.value, props);
            break;
          }

          case "function": {
            newNode = doc.create(FunctionNodeModel, n.value, {
              ...props,
              functionView: main.is(FunctionNodeModel) ? main.functionView : undefined,
            });
            break;
          }

          default: {
            break;
          }
        }

        if (newNode) {
          const [updatedNode] = editNode(n, newNode);
          updatedNodes.push(updatedNode as ViewerNode);
        }
      });
    },
    "setNodeType",
    { runInAction: true },
  );

  if (newNode && updatedNodes.length > 0) {
    copyFieldsFromNode(newNode, updatedNodes);
  }
}

export function copyFieldsFromNode(sourceNode: ViewerNode, targetNode: ViewerNode | ViewerNode[]) {
  const { doc } = sourceNode;
  const nodes = Array.isArray(targetNode) ? targetNode : [targetNode];
  const [originalNode] = getNodesByValue(sourceNode).filter((n) => n.id !== sourceNode.id);

  if (!originalNode) {
    return;
  }

  const { color, name } = originalNode;

  doc.transact(
    () => {
      nodes.forEach((node) => {
        if (color) {
          node.color = color;
        }

        node.name = name;
      });
    },
    "copyFieldsFromNode",
    { runInAction: true },
  );
}
